import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/Badge';
import { Plus, Copy, Gift, Trash2, RefreshCw, Eye, Pencil, Info, ExternalLink, CheckCircle2 } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface Offer {
  id: number;
  name: string;
  networkName: string | null;
  templateUrl: string;
  status: string;
  postbackUrl: string | null;
  postbackAuthType: 'none' | 'hmac' | 'token' | 'ip_whitelist';
  postbackAuthValue: string | null;
  postbackParamClickId: string;
  postbackParamRevenue: string;
  postbackParamStatus: string;
  postbackParamSubId: string | null;
  postbackStatusApproved: string;
  postbackStatusRejected: string;
}

// Postback host used to render copy-paste URLs in the UI. Override per build via
// VITE_POSTBACK_HOST so swapping the postback worker domain doesn't need a code edit.
const POSTBACK_HOST = (import.meta.env.VITE_POSTBACK_HOST as string | undefined)?.replace(/\/+$/, '')
  ?? 'https://rj-postback.jelitanadea77.workers.dev';

function buildPostbackUrl(o: Offer): string {
  // {macro} placeholders that user will customize when pasting to network.
  // offer_name + goal are extra metadata fields any network with rich macros (Trafee
  // {name}/{goal}, iMonet {offer_name}/{event}) can populate. They don't affect
  // matching; they enrich the conversion record so the dashboard can show what the
  // network actually called the offer + which payout model fired.
  const cid = o.postbackParamClickId || 'click_id';
  const rev = o.postbackParamRevenue || 'revenue';
  const st = o.postbackParamStatus || 'status';
  const sub = (o.postbackParamSubId ?? 'sub_id').trim() || 'sub_id';
  return (
    `${POSTBACK_HOST}/pb/${o.id}` +
    `?${cid}={${cid}}` +
    `&${sub}={${sub}}` +
    `&${rev}={${rev}}` +
    `&${st}={${st}}` +
    `&offer_name={offer_name}` +
    `&goal={goal}`
  );
}

export function OffersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Offer | null>(null);
  const [helpOpen, setHelpOpen] = useState<number | null>(null);
  const [showSecret, setShowSecret] = useState<number | null>(null);

  const list = useQuery({
    queryKey: ['offers'],
    queryFn: () => apiClient.get<Offer[]>('/api/v1/offers'),
  });

  const create = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/v1/offers', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      setCreateOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.patch(`/api/v1/offers/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      setEditTarget(null);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/offers/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers'] }),
  });

  const regenSecret = useMutation({
    mutationFn: (id: number) => apiClient.post<{ secret: string }>(`/api/v1/offers/${id}/regenerate-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers'] }),
  });

  const handleDelete = async (offer: { id: number; name: string }) => {
    const result = await confirm({
      title: `Delete ${offer.name}?`,
      description: 'This will permanently remove the offer. Past clicks/conversions keep their offer_id but lose the offer name.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') archive.mutate({ id: offer.id, hard: true });
  };

  const handleRegenSecret = async (offerId: number) => {
    const ok = await confirm({
      title: 'Regenerate HMAC secret?',
      description: 'Only do this if the network requires HMAC auth. Existing secret will stop working immediately.',
      variant: 'warning',
      actions: [{ label: 'Regenerate', variant: 'magenta', result: 'go' }],
    });
    if (ok === 'go') regenSecret.mutate(offerId);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">Offers</h1>
          <p className="text-sm text-text-secondary font-mono">// {list.data?.length ?? 0} network offers</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>New Offer</Button>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Name</th>
                <th>Network</th>
                <th>Track URL</th>
                <th>
                  Postback URL{' '}
                  <span className="font-normal normal-case text-text-tertiary">(paste ke network)</span>
                </th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((o) => {
                const postbackUrl = buildPostbackUrl(o);
                return (
                  <tr key={o.id}>
                    <td>
                      <div className="font-display font-bold text-sm">{o.name}</div>
                      <div className="text-[11px] text-text-tertiary font-mono">#{o.id}</div>
                    </td>
                    <td className="font-mono text-xs">
                      {o.networkName ? (
                        <span className="text-neon-cyan">{o.networkName}</span>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="max-w-xs">
                      <code className="text-[10px] font-mono text-text-secondary bg-bg-tertiary px-2 py-1 rounded block truncate">
                        {o.templateUrl}
                      </code>
                    </td>
                    <td className="max-w-xs">
                      <div className="flex items-center gap-1">
                        <code className="text-[10px] font-mono text-neon-magenta bg-bg-tertiary px-2 py-1 rounded block truncate flex-1">
                          {postbackUrl}
                        </code>
                        <button
                          onClick={() => copyToClipboard(postbackUrl)}
                          className="text-text-tertiary hover:text-neon-cyan p-1"
                          title="Copy postback URL"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          onClick={() => setHelpOpen(helpOpen === o.id ? null : o.id)}
                          className="text-text-tertiary hover:text-neon-cyan p-1"
                          title="Help: how to use this URL"
                        >
                          <Info size={12} />
                        </button>
                      </div>
                      {o.postbackAuthType !== 'none' && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[9px] text-neon-yellow uppercase">{o.postbackAuthType} auth</span>
                          {o.postbackAuthType === 'hmac' && (
                            <button
                              onClick={() => setShowSecret(showSecret === o.id ? null : o.id)}
                              className="text-text-tertiary hover:text-neon-cyan"
                              title="Show secret"
                            >
                              <Eye size={10} />
                            </button>
                          )}
                        </div>
                      )}
                      {showSecret === o.id && o.postbackAuthValue && (
                        <code className="block mt-1 text-[10px] font-mono text-neon-yellow break-all">
                          secret: {o.postbackAuthValue}
                        </code>
                      )}
                      {helpOpen === o.id && <PostbackHelp offer={o} url={postbackUrl} />}
                    </td>
                    <td>
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Pencil size={12} />}
                        onClick={() => setEditTarget(o)}
                        title="Edit offer"
                      />
                      {o.postbackAuthType === 'hmac' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<RefreshCw size={12} />}
                          onClick={() => handleRegenSecret(o.id)}
                          title="Regenerate HMAC secret"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={12} />}
                        onClick={() => handleDelete({ id: o.id, name: o.name })}
                        title="Delete"
                      />
                    </td>
                  </tr>
                );
              })}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-tertiary">
                    <Gift className="mx-auto mb-2" size={32} />
                    <p className="font-mono text-xs">no offers yet — add one to start routing traffic</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* D18: Conditionally mount so React unmounts/remounts the form on open,
           guaranteeing all local state resets to defaults. The useEffect inside
           still handles live-edit sync for the edit modal. */}
      {createOpen && (
        <OfferFormModal
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => create.mutate(d)}
          loading={create.isPending}
        />
      )}
      {editTarget !== null && (
        <OfferFormModal
          open={editTarget !== null}
          mode="edit"
          offer={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => editTarget && update.mutate({ id: editTarget.id, data: d })}
          loading={update.isPending}
        />
      )}
    </div>
  );
}

// ─── Postback Help Panel ──────────────────────────────────────────────────────

function PostbackHelp({ offer, url }: { offer: Offer; url: string }) {
  return (
    <div className="mt-2 p-3 border border-neon-cyan/30 bg-neon-cyan/5 rounded space-y-2 text-[11px] font-mono leading-relaxed">
      <div className="flex items-start gap-2">
        <Info size={12} className="text-neon-cyan shrink-0 mt-0.5" />
        <div className="text-text-primary">
          <strong className="text-neon-cyan">Cara pakai postback URL ini:</strong>
          <ol className="mt-1 ml-3 space-y-0.5 list-decimal text-text-secondary">
            <li>Login ke {offer.networkName || 'network'} → cari setting <em>S2S Postback URL / Conversion API</em></li>
            <li>Paste URL di atas</li>
            <li>
              Ganti placeholder <code className="text-neon-yellow">{`{${offer.postbackParamClickId}}`}</code>{' '}
              dengan macro click_id dari network
            </li>
            <li>
              Ganti <code className="text-neon-yellow">{`{${offer.postbackParamRevenue}}`}</code> dengan macro
              revenue/payout
            </li>
            <li>
              Ganti <code className="text-neon-yellow">{`{${offer.postbackParamStatus}}`}</code> dengan macro status
              (kalau network kasih)
            </li>
          </ol>
        </div>
      </div>
      <div className="border-t border-border-subtle pt-2 text-text-tertiary">
        <strong className="text-text-secondary">Contoh macro di network populer:</strong>
        <table className="mt-1 w-full text-[10px]">
          <thead>
            <tr className="text-text-tertiary">
              <th className="text-left pr-2">Network</th>
              <th className="text-left pr-2">Click ID</th>
              <th className="text-left pr-2">Revenue</th>
              <th className="text-left pr-2">Status</th>
              <th className="text-left pr-2">Offer name</th>
              <th className="text-left">Goal/Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-neon-cyan">iMonetizeIt</td>
              <td><code>{'{click_id}'}</code></td>
              <td><code>{'{payout}'}</code></td>
              <td><code>{'{event}'}</code></td>
              <td><code>{'{offer_name}'}</code></td>
              <td><code>{'{event}'}</code></td>
            </tr>
            <tr>
              <td className="text-neon-cyan">Trafee</td>
              <td><code>{'{ext_click_id}'}</code></td>
              <td><code>{'{sum}'}</code></td>
              <td className="text-text-tertiary">hardcode <code>approved</code></td>
              <td><code>{'{name}'}</code></td>
              <td><code>{'{goal}'}</code></td>
            </tr>
            <tr>
              <td className="text-neon-cyan">Adcash</td>
              <td><code>[click_id]</code></td>
              <td><code>[payout]</code></td>
              <td><code>[status]</code></td>
              <td className="text-text-tertiary">—</td>
              <td className="text-text-tertiary">—</td>
            </tr>
            <tr>
              <td className="text-neon-cyan">AdMaven</td>
              <td><code>{'{click_id}'}</code></td>
              <td><code>{'{payout}'}</code></td>
              <td><code>{'{status}'}</code></td>
              <td className="text-text-tertiary">—</td>
              <td className="text-text-tertiary">—</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="border-t border-border-subtle pt-2 flex items-center gap-2">
        <CheckCircle2 size={11} className="text-neon-green" />
        <span className="text-text-secondary">
          Tip: kalau network pakai nama parameter beda dari "click_id"/"revenue"/"status",
          edit offer ini → tab Postback → ganti param mapping.
        </span>
      </div>
      <a
        href="https://rj-dashboard-4ak.pages.dev/postback-logs"
        className="inline-flex items-center gap-1 text-neon-cyan hover:underline text-[10px] mt-1"
      >
        <ExternalLink size={10} /> Lihat postback yang masuk di /postback-logs
      </a>
    </div>
  );
}

// ─── Form Modal (Create + Edit) ───────────────────────────────────────────────

interface OfferFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  offer?: Offer | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading: boolean;
}

function OfferFormModal({ open, mode, offer, onClose, onSubmit, loading }: OfferFormProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [networkName, setNetworkName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Postback param mapping (advanced)
  const [paramClickId, setParamClickId] = useState('click_id');
  const [paramSubId, setParamSubId] = useState('sub_id');
  const [paramRevenue, setParamRevenue] = useState('revenue');
  const [paramStatus, setParamStatus] = useState('status');
  const [statusApproved, setStatusApproved] = useState('approved');
  const [statusRejected, setStatusRejected] = useState('rejected');

  // Sync state from offer prop whenever modal opens with a different offer
  useEffect(() => {
    if (open && mode === 'edit' && offer) {
      setName(offer.name);
      setUrl(offer.templateUrl);
      setNetworkName(offer.networkName ?? '');
      setParamClickId(offer.postbackParamClickId);
      setParamSubId(offer.postbackParamSubId ?? 'sub_id');
      setParamRevenue(offer.postbackParamRevenue);
      setParamStatus(offer.postbackParamStatus);
      setStatusApproved(offer.postbackStatusApproved);
      setStatusRejected(offer.postbackStatusRejected);
    } else if (open && mode === 'create') {
      setName('');
      setUrl('');
      setNetworkName('');
      setParamClickId('click_id');
      setParamSubId('sub_id');
      setParamRevenue('revenue');
      setParamStatus('status');
      setStatusApproved('approved');
      setStatusRejected('rejected');
      setShowAdvanced(false);
    }
  }, [open, mode, offer]);

  const handleClose = () => {
    if (mode === 'create') {
      setName('');
      setUrl('');
      setNetworkName('');
      setShowAdvanced(false);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={mode === 'edit' ? `Edit Offer: ${offer?.name}` : 'Add Network Offer'}
      size="lg"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const payload: any = {
            name,
            networkName: networkName || undefined,
            templateUrl: url,
            postbackParamClickId: paramClickId,
            postbackParamSubId: paramSubId,
            postbackParamRevenue: paramRevenue,
            postbackParamStatus: paramStatus,
            postbackStatusApproved: statusApproved,
            postbackStatusRejected: statusRejected,
          };
          if (mode === 'create') {
            payload.payoutType = 'cpa';
            payload.payoutValue = 0;
            payload.postbackAuthType = 'none';
          }
          onSubmit(payload);
        }}
        className="space-y-4"
      >
        {mode === 'create' && (
          <div className="px-3 py-2 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed">
            💡 An offer = a deal at a CPA network (iMonetizeIt, Trafee, dll). Network mengirim revenue + status via postback.
          </div>
        )}

        <Input
          label="Offer Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Trafee Smartlink US"
          required
        />

        <Input
          label="Network (optional)"
          value={networkName}
          onChange={(e) => setNetworkName(e.target.value)}
          placeholder="iMonetizeIt / Trafee / Adcash / ..."
          hint="For organizing offers by source network"
        />

        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Track URL (where to redirect)
          </label>
          <textarea
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://network.com/click?aff=12345&subid={sub_id}&clickid={click_id}"
            required
            rows={3}
            className="input-cyber font-mono text-xs"
          />
          <p className="text-[10px] text-text-tertiary mt-1 font-mono">
            Macros: {'{click_id}'} {'{sub_id}'} {'{country}'} {'{ip}'} {'{device}'} {'{os}'} {'{source}'}
          </p>
        </div>

        {/* Advanced: Postback Param Mapping */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-[11px] font-display uppercase tracking-wider text-neon-magenta hover:text-neon-cyan transition-colors"
        >
          <span className={showAdvanced ? 'rotate-90' : ''}>▶</span>
          Advanced: Postback Param Mapping
        </button>

        {showAdvanced && (
          <div className="pl-4 border-l-2 border-neon-magenta/30 space-y-3 animate-fade-in">
            <div className="text-[10px] font-mono text-text-tertiary leading-relaxed space-y-1.5 px-2 py-2 border border-neon-yellow/30 bg-neon-yellow/5 rounded">
              <p className="text-neon-yellow">
                <strong>⚠ Yang diisi di sini = nama param di URL postback, BUKAN macro network.</strong>
              </p>
              <p>
                Worker baca <code className="text-neon-cyan">params[nama_field_ini]</code> dari URL yang masuk.
              </p>
              <p>
                Contoh URL postback di Torazzo:<br />
                <code className="text-neon-cyan break-all">.../pb/4?<u>click_id</u>={'{p2}'}&<u>sub_id</u>={'{p1}'}&...</code>
              </p>
              <p>
                → Field di sini diisi: <code className="text-neon-green">click_id</code>, <code className="text-neon-green">sub_id</code> (bagian sebelum <code>=</code>).<br />
                ✗ Jangan isi <code className="text-neon-red line-through">{'{p2}'}</code> / <code className="text-neon-red line-through">{'{p1}'}</code> — itu macro Torazzo, bukan nama param.
              </p>
              <p>Biarkan default semua kalau pakai URL postback yang sudah di-generate (di atas).</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input
                label="Click ID param"
                value={paramClickId}
                onChange={(e) => setParamClickId(e.target.value)}
                hint="default: click_id"
              />
              <Input
                label="Sub ID param"
                value={paramSubId}
                onChange={(e) => setParamSubId(e.target.value)}
                hint="Trafee: ext_click_id · iMonet: aff_sub"
              />
              <Input
                label="Revenue param"
                value={paramRevenue}
                onChange={(e) => setParamRevenue(e.target.value)}
                hint="Trafee: sum · default: revenue"
              />
              <Input
                label="Status param"
                value={paramStatus}
                onChange={(e) => setParamStatus(e.target.value)}
                hint="default: status"
              />
            </div>
            <p className="text-[10px] font-mono text-text-tertiary">
              Status value mapping (yang dianggap approved/rejected):
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Approved value"
                value={statusApproved}
                onChange={(e) => setStatusApproved(e.target.value)}
                hint="contoh: approved, 1, confirmed"
              />
              <Input
                label="Rejected value"
                value={statusRejected}
                onChange={(e) => setStatusRejected(e.target.value)}
                hint="contoh: rejected, 0, declined"
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
          <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={loading} icon={mode === 'edit' ? <Pencil size={14} /> : <Plus size={14} />}>
            {mode === 'edit' ? 'Save Changes' : 'Add Offer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
